import { useEffect, useRef, type ClipboardEvent, type KeyboardEvent } from 'react';

import { cn } from '../../lib/utils';

interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
  length?: number;
}

export function OtpInput({ value, onChange, error, disabled = false, length = 6 }: OtpInputProps) {
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  // Derive individual digits from value prop
  const digits = value.split('').slice(0, length);

  // Auto-focus first input on mount
  useEffect(() => {
    if (!disabled) {
      inputRefs.current[0]?.focus();
    }
  }, [disabled]);

  const handleChange = (index: number, digit: string) => {
    // Only allow single digit
    if (!/^\d?$/.test(digit)) return;

    const newDigits = [...digits];

    // Pad array to length
    while (newDigits.length < length) {
      newDigits.push('');
    }

    newDigits[index] = digit;
    onChange(newDigits.join(''));

    // Auto-advance focus if digit entered
    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      if (!digits[index] && index > 0) {
        // If current input is empty, move to previous and clear it
        event.preventDefault();
        const newDigits = [...digits];
        while (newDigits.length < length) {
          newDigits.push('');
        }
        newDigits[index - 1] = '';
        onChange(newDigits.join(''));
        inputRefs.current[index - 1]?.focus();
      }
    }

    if (event.key === 'ArrowLeft' && index > 0) {
      event.preventDefault();
      inputRefs.current[index - 1]?.focus();
    }

    if (event.key === 'ArrowRight' && index < length - 1) {
      event.preventDefault();
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasted = event.clipboardData.getData('text');
    const digitsOnly = pasted.replace(/\D/g, '').slice(0, length);
    onChange(digitsOnly);

    // Focus last filled or first empty
    const focusIndex = Math.min(digitsOnly.length, length - 1);
    inputRefs.current[focusIndex]?.focus();
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    // Select content on focus for easy replacement
    event.target.select();
  };

  return (
    <div>
      <div className='flex justify-center gap-2.5'>
        {Array.from({ length }).map((_, index) => (
          <input
            key={index}
            ref={(el) => {
              inputRefs.current[index] = el;
            }}
            type='text'
            inputMode='numeric'
            pattern='[0-9]'
            maxLength={1}
            value={digits[index] ?? ''}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            onFocus={handleFocus}
            disabled={disabled}
            className={cn(
              'w-14 h-16 text-center text-3xl font-mono border rounded-lg',
              'focus:ring-2 focus:outline-none transition-colors',
              'bg-white text-brand-gray',
              error ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-primary focus:border-primary',
              disabled && 'opacity-50 cursor-not-allowed bg-gray-100',
            )}
            aria-label={`Digit ${index + 1}`}
            aria-invalid={error ? 'true' : 'false'}
          />
        ))}
      </div>
      {error ? <p className='mt-2 text-xs text-red-600 text-center'>{error}</p> : null}
    </div>
  );
}
